"use client";
import { useEffect, useState, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import EducationMain from "./EducationMain";
import { getAllDegrees } from "../../services/degreeServices";
import { getActiveEducations } from "../../services/educationServices";
import { motion } from "framer-motion";

const Education = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDegree, setSelectedDegree] = useState(null);
  const [limit] = useState(8);
  const educationalProgramsRef = useRef(null);
  const hasInitiallyLoaded = useRef(false);
  const shouldScrollRef = useRef(false);
  const filterChangedRef = useRef(false);

  useEffect(() => {
    document.title = "Education | Society of Aquaculture Professionals";

    const urlParams = new URLSearchParams(window.location.search);
    const pageFromUrl = parseInt(urlParams.get("page")) || 1;
    const degreeFromUrl = urlParams.get("degreeId") || null;

    setCurrentPage(pageFromUrl);
    setSelectedDegree(degreeFromUrl);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", currentPage.toString());

    if (selectedDegree) {
      url.searchParams.set("degreeId", selectedDegree);
    } else {
      url.searchParams.delete("degreeId");
    }

    window.history.replaceState({}, "", url);
  }, [currentPage, selectedDegree]);

  const {
    data: degrees,
    isLoading: degreesLoading,
    isError: degreesError,
    error: degreesErrMsg,
  } = useQuery({
    queryKey: ["degrees"],
    queryFn: getAllDegrees,
  });

  const {
    data: educationsData,
    isLoading: educationsLoading,
    isError: educationsError,
    error: educationsErrMsg,
    isFetching,
    isSuccess,
  } = useQuery({
    queryKey: ["educations", currentPage, selectedDegree, limit],
    queryFn: () =>
      getActiveEducations({
        page: currentPage,
        limit,
        degreeId: selectedDegree,
      }),
    keepPreviousData: true,
  });

  const scrollToEducationalPrograms = useCallback(() => {
    if (educationalProgramsRef.current) {
      setTimeout(() => {
        const element = educationalProgramsRef.current;
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 100;
          const scrollTop = rect.top + window.scrollY - offset;

          window.scrollTo({
            top: scrollTop,
            behavior: "smooth",
          });
        }
      }, 50);
    }
  }, []);

  useEffect(() => {
    if (isSuccess && educationsData && !isFetching) {
      const shouldScroll =
        shouldScrollRef.current ||
        filterChangedRef.current ||
        (hasInitiallyLoaded.current && currentPage > 1);

      if (shouldScroll) {
        shouldScrollRef.current = false;
        filterChangedRef.current = false;

        setTimeout(() => {
          scrollToEducationalPrograms();
        }, 100);
      } else if (!hasInitiallyLoaded.current) {
        hasInitiallyLoaded.current = true;
      }
    }
  }, [
    isSuccess,
    educationsData,
    isFetching,
    currentPage,
    scrollToEducationalPrograms,
  ]);

  const handlePageChange = useCallback((page) => {
    shouldScrollRef.current = true;
    setCurrentPage(page);
  }, []);

  const handleDegreeFilter = useCallback((degreeId) => {
    shouldScrollRef.current = true;
    filterChangedRef.current = true;

    setSelectedDegree(degreeId);
    setCurrentPage(1);

    hasInitiallyLoaded.current = false;
  }, []);

  if (degreesLoading || (educationsLoading && !educationsData)) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  if (degreesError || educationsError) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600">
        {degreesErrMsg?.message ||
          educationsErrMsg?.message ||
          "Something went wrong"}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <Header />
      <EducationMain
        degrees={degrees?.data ?? []}
        educationsData={educationsData}
        isFetching={isFetching}
        currentPage={currentPage}
        selectedDegree={selectedDegree}
        onPageChange={handlePageChange}
        onDegreeFilter={handleDegreeFilter}
        educationalProgramsRef={educationalProgramsRef}
      />
      <Footer />
    </motion.div>
  );
};

export default Education;
